import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { ResolvedAddress } from '../../../services/address-lookup.service';
import { OrganisationService } from '../../../services/organisation.service';
import {
  COUNTRIES,
  HOME_COUNTRY_CODE,
  countryName,
  isEuCountry,
  normalizeVatId,
  resolveCountryCode,
  taxTreatment,
  taxTreatmentKey,
  vatIdError,
} from '../../../utils/countries.util';

/** Billing data check in front of a first purchase.
 *
 *  The invoice is issued to what the organisation has on file — company name,
 *  address, country, VAT ID. Buyers used to reach Stripe Checkout with those
 *  empty or outdated and the first invoice went out wrong. This dialog shows
 *  the stored values, lets the buyer complete them and saves through the same
 *  endpoint as Organisation › Allgemein / Adresse (which also syncs the Stripe
 *  customer). Closes with `true` when the data is saved and the purchase may
 *  continue. */
@Component({
  selector: 'app-billing-details-dialog',
  templateUrl: './billing-details-dialog.component.html',
  styleUrl: './billing-details-dialog.component.scss',
})
export class BillingDetailsDialogComponent implements OnInit, AfterViewInit {
  /** Set when the purchase was already refused for incomplete data: shows why
   *  the dialog opened and marks the missing fields straight away. */
  @Input() incomplete = false;

  countries = COUNTRIES;
  saving = false;
  /** Backend message when the save was rejected (e.g. VAT ID / country mismatch). */
  errorKey?: string;

  private vatIdValidator = (control: AbstractControl) => {
    const country = control.parent?.get('country_code')?.value;
    // An EU business outside Germany is only invoiced without German VAT
    // (reverse charge) with its VAT ID — the backend refuses the purchase
    // without one (VAT_ID_REQUIRED).
    if (taxTreatment(country, control.value) === 'eu_missing_vat_id') {
      return { vatId: 'dialogs.billingDetails.vatRequired' };
    }
    const key = vatIdError(control.value, country);
    return key ? { vatId: key } : null;
  };

  form = this.fb.group({
    company_name: ['', [Validators.required]],
    street: ['', [Validators.required]],
    number: ['', [Validators.required]],
    zip: ['', [Validators.required]],
    city: ['', [Validators.required]],
    country_code: ['', [Validators.required]],
    vat_id: ['', [this.vatIdValidator]],
  });

  constructor(
    public dialogRef: NbDialogRef<BillingDetailsDialogComponent>,
    private fb: FormBuilder,
    private translate: TranslateService,
    private organisationService: OrganisationService,
    private host: ElementRef<HTMLElement>
  ) {}

  ngAfterViewInit(): void {
    if (this.incomplete) {
      this.focusFirstInvalid();
    }
  }

  /** Put the cursor where something is missing, so nobody has to look for it.
   *  Deferred: Angular writes `ng-invalid` and the dialog finishes its own
   *  focus handling first. */
  private focusFirstInvalid() {
    setTimeout(() => {
      const target = this.host.nativeElement.querySelector<HTMLElement>(
        'form input.ng-invalid, form nb-select.ng-invalid button'
      );
      target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      target?.focus();
    });
  }

  /** The server checks the VAT ID once more (format, country prefix). Its
   *  answer belongs under that field, not in a generic error line. */
  private vatIdErrorFromServer(e: HttpErrorResponse): string | null {
    const detail = typeof e?.error?.detail === 'string' ? e.error.detail : '';
    if (!/vat id/i.test(detail)) {
      return null;
    }
    if (/billing\s+country/i.test(detail)) {
      return 'errors.vatId.countryMismatch';
    }
    return /prefix \(e\.g\./i.test(detail)
      ? 'errors.vatId.prefix'
      : 'errors.vatId.format';
  }

  ngOnInit(): void {
    const org = this.organisationService.organisation;
    if (org) {
      this.form.patchValue({
        company_name: org.company_name || '',
        street: org.street || '',
        number: org.number || '',
        zip: org.zip || '',
        city: org.city || '',
        country_code: org.country_code || resolveCountryCode(org.country) || '',
        vat_id: org.vat_id || '',
      });
      // The country code above was derived from the stored country NAME: the
      // record itself has none. Treat that as a change, or an untouched form
      // would close without saving and the checkout would refuse it again.
      if (!org.country_code && resolveCountryCode(org.country)) {
        this.form.markAsDirty();
      }
    }
    if (this.incomplete) {
      this.form.controls.vat_id.updateValueAndValidity();
      this.form.markAllAsTouched();
    }
    // The VAT ID prefix must match the country — re-check when it changes.
    this.form.controls.country_code.valueChanges.subscribe(() =>
      this.form.controls.vat_id.updateValueAndValidity()
    );
  }

  private get lang(): string {
    return this.translate.currentLang || this.translate.defaultLang || 'de';
  }

  countryLabel(code: string): string {
    return countryName(code, this.lang);
  }

  /** EU business outside Germany: the VAT ID is mandatory there. */
  get vatIdRequired(): boolean {
    const code = this.form.controls.country_code.value;
    return !!code && isEuCountry(code) && code !== HOME_COUNTRY_CODE;
  }

  /** i18n key of the VAT that will be charged for what is entered right now;
   *  null until a country is chosen. */
  get taxStatusKey(): string | null {
    const code = this.form.controls.country_code.value;
    if (!code) {
      return null;
    }
    return taxTreatmentKey(taxTreatment(code, this.form.controls.vat_id.value));
  }

  get vatIdErrorKey(): string | null {
    const control = this.form.controls.vat_id;
    return (control.touched && control.errors?.['vatId']) || null;
  }

  applyResolved(address: ResolvedAddress) {
    this.form.patchValue({
      street: address.street || '',
      number: address.number || '',
      zip: address.zip || '',
      city: address.city || '',
      country_code: address.country_code || '',
    });
    this.form.markAsDirty();
  }

  showInputError(name: string) {
    const control = this.form.get(name);
    return control?.touched && control?.invalid;
  }

  confirm() {
    if (this.saving) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.focusFirstInvalid();
      return;
    }
    // Nothing changed: the stored data is already what Stripe has.
    if (this.form.pristine) {
      this.dialogRef.close(true);
      return;
    }

    const value = this.form.getRawValue();
    this.saving = true;
    this.errorKey = undefined;
    this.organisationService
      .updateMyOrganisation({
        company_name: (value.company_name || '').trim(),
        street: (value.street || '').trim(),
        number: (value.number || '').trim(),
        zip: (value.zip || '').trim(),
        city: (value.city || '').trim(),
        country_code: value.country_code || '',
        // `country` is the display column other views read.
        country: countryName(value.country_code || '', this.lang),
        // Empty string, never undefined — undefined is dropped from the JSON
        // and the old value would survive server-side.
        vat_id: normalizeVatId(value.vat_id) || '',
      }, true)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: (e: HttpErrorResponse) => {
          const vatKey = this.vatIdErrorFromServer(e);
          if (vatKey) {
            const vat = this.form.controls.vat_id;
            vat.setErrors({ vatId: vatKey });
            vat.markAsTouched();
            this.focusFirstInvalid();
            return;
          }
          this.errorKey = 'dialogs.billingDetails.saveFailed';
        },
      });
  }
}
